/**
 * How long a ticket has been waiting, and what that means.
 *
 * WHY THIS IS THE MOST IMPORTANT THING ON THE SCREEN
 * --------------------------------------------------
 * A kitchen display is read from across a room by someone with their hands
 * full. Nobody reads the timestamps. What they see is which tickets have gone
 * from calm to loud, and the whole screen is only useful if that transition
 * happens at the right moment.
 *
 * The thresholds are minutes rather than a target per dish, because a ticket
 * is finished when its slowest item is — and a customer waiting twenty minutes
 * does not care that the pizza is genuinely a twenty-minute pizza.
 */

/** Fine. */
export const CALM = 'calm';
/** Taking a while — worth glancing at. */
export const WARM = 'warm';
/** Late. Somebody is waiting and nobody has told them why. */
export const LATE = 'late';

const WARM_AFTER_MINUTES = 10;
const LATE_AFTER_MINUTES = 20;

/**
 * @param {number} minutes  how long since the order was placed
 * @returns {'calm'|'warm'|'late'}
 */
export function urgency(minutes) {
  const waited = Number(minutes);

  /**
   * Only a figure that is genuinely large reads as late.
   *
   * A missing or nonsense value falls through to calm on its own, because
   * every comparison against NaN is false — there is no separate guard for it,
   * and adding one would suggest a check that changes nothing. What matters is
   * the direction: the tests pin bad data to calm, so an implementation that
   * defaulted the other way would fail.
   *
   * A screen that cries wolf on bad data is one people stop looking at, and
   * then the genuinely late ticket goes unnoticed too.
   */
  if (waited >= LATE_AFTER_MINUTES) return LATE;
  if (waited >= WARM_AFTER_MINUTES) return WARM;
  return CALM;
}

/**
 * The wait, written for a glance.
 *
 * Minutes up to an hour, then hours — because "94m" is a number to work out
 * and "1h 34m" is one to read. Anything past an hour on a kitchen screen means
 * something has gone wrong anyway.
 */
export function waited(minutes) {
  const value = Number(minutes);
  if (!Number.isFinite(value) || value < 0) return '—';

  const whole = Math.floor(value);
  if (whole < 60) return `${whole}m`;

  const hours = Math.floor(whole / 60);
  const rest = whole % 60;
  return rest === 0 ? `${hours}h` : `${hours}h ${rest}m`;
}
